import React, { PureComponent, Fragment } from 'react';
import Intercom from 'react-intercom';
import PropTypes from 'prop-types';
import { IntlProvider, FormattedMessage } from 'react-intl';
import theme from '../theme/styled';
import { Main } from './app.styles';
import { ResponsiveThemeProvider as ThemeProvider } from '../shared/components/responsiveThemeProvider';

import { translationMessages, DEFAULT_LOCALE } from '../i18n';
import { ROUTES } from './routes';
import { GlobalStyle } from '../theme/global';
import messages from './app.messages';
import { HeaderBar } from '../shared/components/headerBar';
import { SnackbarRender } from '../shared/components/snackbarRender';
import TermsOverlay from '../shared/components/termsOverlay/termsOverlay.container';
import initializeFontFace from '../theme/initializeFontFace';
import { PrivacyOverlay } from '../shared/components/privacyOverlay';
import { CookiesOverlay } from '../shared/components/cookiesOverlay';
import { IdleTimer } from '../shared/components/idleTimer';
import { Footer } from '../shared/components/footer';
import * as GTM from '../shared/services/gtm';
import { Helmet } from '../shared/components/helmet';
import { AuthOverlay } from '../shared/components/authOverlay';
import { renderWhenTrue } from '../shared/utils/rendering';
import { FilterModal } from '../shared/components/filterModal';
import { CookiesBar } from '../shared/components/cookiesBar';

export class App extends PureComponent {
  static propTypes = {
    children: PropTypes.node,
    isInitialised: PropTypes.bool,
    isSignedIn: PropTypes.bool,
    isRehydrated: PropTypes.bool,
    isFooterInitialised: PropTypes.bool,
    language: PropTypes.string,
    location: PropTypes.object,
    match: PropTypes.object.isRequired,
    setLanguage: PropTypes.func.isRequired,
    startup: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    userIntercomData: PropTypes.object.isRequired,
  };

  componentDidMount() {
    this.prevPathname = this.props.location.pathname;
    this.removeHistoryListener = this.props.history.listen(this.handleHistoryChange);
    this.props.setLanguage(this.getLanguage(this.props));
    this.props.startup();
    initializeFontFace();
  }

  componentDidUpdate(prevProps) {
    if (this.getLanguage(prevProps) !== this.getLanguage(this.props)) {
      this.props.setLanguage(this.getLanguage(this.props));
    }
  }

  componentWillUnmount() {
    this.removeHistoryListener();
  }

  getLanguage = props => props.match.params.lang || DEFAULT_LOCALE;

  prevPathname = '';

  handleHistoryChange = ({ pathname }) => {
    GTM.logRouteChange(pathname);
    this.prevPathname = pathname;
  };

  render() {
    const isNotRehydratedKiosk =
      this.props.location.pathname.indexOf(ROUTES.kioskBase) === 0 && !this.props.isRehydrated;

    const userIntercomData = this.props.isSignedIn ? this.props.userIntercomData : null;

    if (!this.props.language || isNotRehydratedKiosk) {
      return null;
    }

    return (
      <IntlProvider locale={this.props.language} messages={translationMessages[this.props.language]}>
        <ThemeProvider theme={theme}>
          <Fragment>
            <Intercom appID={process.env.REACT_APP_INTERCOM_ID} {...userIntercomData} />

            <IdleTimer />

            <FormattedMessage {...messages.pageTitle}>
              {pageTitle => (
                <Helmet
                  noTitle
                  reactHelmetProps={{
                    titleTemplate: `%s - ${pageTitle}`,
                    defaultTitle: pageTitle,
                  }}
                />
              )}
            </FormattedMessage>

            <GlobalStyle />

            <HeaderBar />

            <Main>{React.Children.only(this.props.children)}</Main>

            {renderWhenTrue(() => <Footer />)(this.props.isFooterInitialised)}

            <SnackbarRender />

            <CookiesBar />

            <TermsOverlay />
            <PrivacyOverlay />
            <CookiesOverlay />
            <AuthOverlay />
            <FilterModal />
          </Fragment>
        </ThemeProvider>
      </IntlProvider>
    );
  }
}
